/**
 * #603 agent-day-1 A/B test. The server owns assignment and visibility (op `state` of the
 * agent-day1-prompt action checks the kill switch, the arm, the day window and whether the
 * user already has an agent). The client only adds what the server can't know: the current
 * route and whether the user already acted on the prompt in this session.
 */

export type AgentDay1PromptState =
  | { show: true; showNumber: 1 | 2 }
  | { show: false }
  | { error: string };

export type AgentDay1PromptOp = 'shown' | 'clicked' | 'dismissed';

/** Onboarding owns the screen there, and inside /agents the user is already where the prompt leads. */
export const isSuppressedPath = (pathname: string): boolean =>
  /^\/(onboarding|agents)(\/|$)/.test(pathname);

export interface PageLoadFlags { userId: string | undefined; shown: boolean; closed: boolean }
let pageLoad: PageLoadFlags = { userId: undefined, shown: false, closed: false };

/**
 * What already happened to the prompt in this page load: shown (one `shown` call; the server's
 * compare-and-set dedupes across tabs and reloads) and closed (clicked or dismissed, so a remount
 * on route change doesn't bring it back). Keyed to the user, because logout is an in-app
 * navigation: the next account in the same tab must start clean.
 */
export function pageLoadFlagsFor(userId: string | undefined): PageLoadFlags {
  if (pageLoad.userId !== userId) pageLoad = { userId, shown: false, closed: false };
  return pageLoad;
}

export function shouldRenderAgentDay1Prompt(input: {
  state: AgentDay1PromptState | { show: true } | undefined;
  pathname: string;
  closed: boolean;
  /** Already rendered in this page load. Recording the show makes the server answer hide, so a
   *  later refetch (auth re-init, language change) must not take the card away mid-session. */
  shownThisLoad?: boolean;
}): boolean {
  const { state, pathname, closed, shownThisLoad } = input;
  if (closed || isSuppressedPath(pathname)) return false;
  if (shownThisLoad) return true;
  // Fail closed: loading, error and malformed payloads all render nothing.
  if (!state || !('show' in state) || state.show !== true) return false;
  return 'showNumber' in state && (state.showNumber === 1 || state.showNumber === 2);
}
